import React from 'react';
import { Icon } from './Icon.jsx';

/**
 * v2.0 (A1) — Logging principles.
 *
 * Two surfaces share one list:
 *   • <PrinciplesCard/>  — slim onboarding card slotted into Today on first
 *                          session. Dismissible. Per-user persistence via
 *                          localStorage (App.jsx owns the gate).
 *   • <PrinciplesModal/> — full modal reachable from UserMenu at any time.
 *
 * The six principles are the authoritative copy; both surfaces render them
 * verbatim. Keep edits in one place.
 */
export const LOGGING_PRINCIPLES = [
  {
    key: 'Log your time daily.',
    desc: 'Catching up at month-end never works — by then you’ve forgotten the details.',
  },
  {
    key: 'Tidy up at the end of each week.',
    desc: 'Five minutes on Friday catches gaps while context is fresh.',
  },
  {
    key: 'Your month closes on the 5th of the following month.',
    desc: 'After your manager approves, the month is locked. Filling this data in time is critical.',
  },
  {
    key: 'Absences sync from Calamari automatically.',
    desc: 'Log SL/VL properly in Calamari — in advance whenever you can. ClockIn will pick them up.',
  },
  {
    key: 'Granularity is 15 minutes.',
    desc: 'Shorter activities usually belong in a bigger bucket — e.g. “standup + ad-hoc syncs”.',
  },
  {
    key: 'Use Import ▾ when you can.',
    desc: 'Calendar + Jira speed this up dramatically when both are kept clean.',
  },
];

function PrinciplesList() {
  return (
    <ol
      style={{
        margin: 0,
        padding: 0,
        listStyle: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 22,
      }}
    >
      {LOGGING_PRINCIPLES.map((p, i) => (
        <li
          key={i}
          style={{ position: 'relative', paddingLeft: 56, minHeight: 44 }}
        >
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: 38,
              height: 38,
              borderRadius: '50%',
              background: '#fff',
              border: '1.5px solid var(--sp-mint-100)',
              color: '#007C7C',
              fontWeight: 700,
              fontSize: 15,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '-0.02em',
            }}
          >
            {i + 1}
          </span>
          <div
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: 'var(--sp-fg-1)',
              lineHeight: 1.35,
              letterSpacing: '-0.01em',
            }}
          >
            {p.key}
          </div>
          <div
            style={{
              fontSize: 14,
              color: 'var(--sp-fg-2)',
              lineHeight: 1.6,
              marginTop: 6,
            }}
          >
            {p.desc}
          </div>
        </li>
      ))}
    </ol>
  );
}

export function PrinciplesCard({ onDismiss, onLearnMore }) {
  return (
    <div
      style={{
        margin: '0 0 18px',
        padding: '18px 22px 18px 20px',
        borderRadius: 16,
        background:
          'linear-gradient(180deg, var(--sp-mint-100) 0%, #F5FFFD 100%)',
        border: '1px solid #B2F7ED',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 14,
        position: 'relative',
        animation: 'tt-fade-in 220ms ease',
      }}
    >
      <span
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          flex: 'none',
          background: '#fff',
          color: '#007C7C',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
        }}
      >
        <Icon name="sparkle" size={16} stroke={2.2} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <h3
          style={{
            margin: 0,
            fontSize: 15,
            fontWeight: 700,
            color: '#013F35',
            letterSpacing: '-0.005em',
          }}
        >
          Welcome to ClockIn — a few logging principles
        </h3>
        <p
          style={{
            margin: '4px 0 12px',
            fontSize: 13,
            color: '#026153',
            lineHeight: 1.5,
          }}
        >
          Reading this once will save you time later. Always reachable from
          the user menu.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onDismiss} className="btn btn-primary btn-sm">
            Got it
          </button>
          <button onClick={onLearnMore} className="btn btn-secondary btn-sm">
            Read all six
          </button>
        </div>
      </div>
      <button
        onClick={onDismiss}
        className="btn btn-ghost btn-icon"
        title="Dismiss"
        style={{ position: 'absolute', top: 10, right: 10, color: '#026153' }}
      >
        <Icon name="x" size={14} />
      </button>
    </div>
  );
}

export function PrinciplesModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div className="tt-modal-backdrop" onClick={onClose}>
      <div
        className="tt-modal"
        style={{ maxWidth: 760, marginTop: '5vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: '28px 32px 22px',
            background:
              'linear-gradient(180deg, var(--sp-mint-100) 0%, #F5FFFD 100%)',
            borderRadius: '18px 18px 0 0',
            borderBottom: '1px solid #B2F7ED',
            position: 'relative',
          }}
        >
          <button
            className="btn btn-ghost btn-icon"
            onClick={onClose}
            style={{
              position: 'absolute',
              top: 14,
              right: 14,
              color: '#026153',
            }}
          >
            <Icon name="x" size={16} />
          </button>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: '#026153',
              marginBottom: 6,
            }}
          >
            ClockIn principles
          </div>
          <h2
            style={{
              margin: 0,
              fontSize: 24,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              color: '#013F35',
            }}
          >
            Six habits to keep your time clean.
          </h2>
          <p
            style={{
              margin: '8px 0 0',
              fontSize: 14,
              color: '#026153',
              lineHeight: 1.5,
            }}
          >
            Read once, follow daily. Always reachable from the user menu.
          </p>
        </div>
        <div style={{ padding: '28px 32px 24px' }}>
          <PrinciplesList />
        </div>
        <div
          style={{
            padding: '14px 32px 22px',
            borderTop: '1px solid var(--sp-border-soft)',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button className="btn btn-primary" onClick={onClose}>
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
